import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MoreHorizontal } from 'lucide-react';
import { removeMember, resendInvite } from '@/services/teamService';
import { toast } from 'sonner';
import SelectRoleModal from './SelectRoleModal';
import { useAuth } from '@/contexts/AuthContext';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';
import { logError } from '@/utils/errorHandler';

interface ManageControlsProps {
  membershipId: string | number;
  status: 'active' | 'pending';
  currentRoleId: string | number;
  roleName: string;
  isCurrentUser?: boolean;
}

export const ManageControls: React.FC<ManageControlsProps> = ({
  membershipId,
  status,
  currentRoleId: _currentRoleId,
  roleName,
  isCurrentUser,
}) => {
  // Get translations
  const i18n = useTeampageTranslations();

  const { user, checkPermission } = useAuth();
  const orgId = user?.organization_id;
  const queryClient = useQueryClient();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  // Check permissions for the various actions
  const canInvite = checkPermission('team.invite');
  const canChangeRole =
    checkPermission('team.change_role') && !isCurrentUser && roleName !== 'OrgOwner';
  const canRemove = checkPermission('team.remove') && !isCurrentUser && roleName !== 'OrgOwner';

  const handleRemove = async () => {
    try {
      if (!orgId) {
        toast.error(i18n.errors.organizationIdMissing);
        return;
      }

      // Additional check to ensure user has permission
      if (!canRemove) {
        toast.error(i18n.errors.noPermissionRemove);
        return;
      }

      await removeMember(membershipId, orgId);
      toast.success(
        status === 'pending' ? i18n.success.inviteCanceled : i18n.success.memberRemoved
      );
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
    } catch (error: any) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ManageControls',
        action: 'handleRemove',
        metadata: {
          membershipId,
          status,
          roleName,
          context: 'member_removal_error',
        },
      });
      toast.error(i18n.errors.errorRemoving(error.message));
    }
  };

  const handleResend = async () => {
    try {
      if (!orgId) {
        toast.error(i18n.errors.organizationIdMissing);
        return;
      }

      // Additional check to ensure user has permission
      if (!canInvite) {
        toast.error(i18n.errors.noPermissionInvite);
        return;
      }

      await resendInvite(membershipId, orgId);
      toast.success(i18n.success.invitationResent);
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
    } catch (error: any) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ManageControls',
        action: 'handleResend',
        metadata: {
          membershipId,
          status,
          roleName,
          context: 'invitation_resend_error',
        },
      });
      toast.error(i18n.errors.errorResending(error.message));
    }
  };

  return (
    <div>
      {/* Role change modal - render conditionally when needed */}
      {canChangeRole && isRoleModalOpen && (
        <SelectRoleModal
          membershipId={membershipId}
          currentRoleName={roleName}
          open={isRoleModalOpen}
          onClose={() => setIsRoleModalOpen(false)}
        />
      )}

      {status === 'pending' ? (
        // For pending members, show direct action buttons
        <div className="flex space-x-2">
          {canInvite && (
            <Button variant="outline" size="sm" onClick={handleResend}>
              {i18n.buttons.resend}
            </Button>
          )}
          {canRemove && (
            <Button variant="destructive" size="sm" onClick={handleRemove}>
              {i18n.buttons.cancel}
            </Button>
          )}
        </div>
      ) : // For active members, show dropdown menu (but not for OrgOwner)
      roleName === 'OrgOwner' ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled
                className="cursor-not-allowed text-gray-400"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{i18n.manageControls.cannotRemoveOrgOwner}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {checkPermission('team.change_role') && (
              <>
                {isCurrentUser ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem disabled className="cursor-not-allowed text-gray-400">
                          {i18n.manageControls.changeRole}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{i18n.manageControls.cannotChangeOwnRole}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <DropdownMenuItem
                    onSelect={e => {
                      e.preventDefault();
                      setIsRoleModalOpen(true);
                    }}
                  >
                    {i18n.manageControls.changeRole}
                  </DropdownMenuItem>
                )}
              </>
            )}
            {checkPermission('team.remove') && (
              <>
                {checkPermission('team.change_role') && <DropdownMenuSeparator />}
                {isCurrentUser ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem disabled className="cursor-not-allowed text-gray-400">
                          {i18n.manageControls.removeMember}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{i18n.manageControls.cannotRemoveYourself}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <DropdownMenuItem
                    className="text-red-600 focus:bg-red-600 focus:text-red-50"
                    onSelect={handleRemove}
                  >
                    {i18n.manageControls.removeMember}
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default ManageControls;
